import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, FileCheck2, Sparkles, ThumbsDown, ThumbsUp, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { ACTIVITY_LABELS, AI_STATUS, APPR_STATUS, EV_STATUS, fmtDate, fmtDateTime } from "../lib/labels";
import { userName, useUsers } from "../lib/hooks";
import { Chip, ReasonsList } from "./common";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EVIDENCE_TYPES = ["documento", "email", "firma", "certificado", "justificante", "fotografia", "comentario", "aprobacion", "registro_externo"];

export function BlockedDialog({ open, onClose, reasons, title = "Acción bloqueada" }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="blocked-action-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Esta acción no puede ejecutarse todavía. Esto es lo que falta y quién debe resolverlo:</p>
        <ReasonsList reasons={reasons || []} />
      </DialogContent>
    </Dialog>
  );
}

export function ActivityTimeline({ items, testid }) {
  const { map } = useUsers();
  if (!items?.length) return <p className="py-6 text-center text-sm text-slate-500">Sin actividad registrada.</p>;
  return (
    <ol className="relative space-y-4 border-l border-slate-200 pl-5" data-testid={testid || "activity-timeline"}>
      {items.map((a) => (
        <li key={a.id} className="relative">
          <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-slate-400" />
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium text-slate-800">{ACTIVITY_LABELS[a.action] || a.action}</span>
            <span className="text-xs text-slate-400">{userName(map, a.actor_id)} · {fmtDateTime(a.timestamp)}</span>
            {a.rule && <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{String(a.rule).replace(/_/g, " ")}</span>}
          </div>
          <p className="mt-0.5 text-sm text-slate-600">{a.detail}</p>
        </li>
      ))}
    </ol>
  );
}

export function EvidenceReqCard({ req, me, onChanged }) {
  const { map } = useUsers();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [form, setForm] = useState({ type: req.expected_type || "documento", title: "", description: "" });
  const latest = (req.evidences || [])[0];
  const canSubmit = ["pendiente", "solicitada", "rechazada"].includes(req.status) && (req.responsible_id === me?.id || !me?.roles?.includes("VIEWER"));
  const canReview = req.status === "en_revision" && latest && (req.reviewer_id === me?.id || me?.roles?.includes("REVIEWER") || me?.roles?.includes("DEMO_ADMIN")) && req.responsible_id !== me?.id;

  const doSubmit = async () => {
    try {
      await api.post(`/evidence-requirements/${req.id}/submit`, { ...form, title: form.title || req.name });
      toast.success("Evidencia aportada y enviada a revisión");
      setSubmitOpen(false);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doReview = async (decision) => {
    try {
      await api.post(`/evidences/${latest.id}/review`, { decision, motivo });
      toast.success(decision === "aceptar" ? "Evidencia aceptada" : "Evidencia rechazada");
      setRejectOpen(false);
      setMotivo("");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="ts-card p-4" data-testid={`evidence-req-${req.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <FileCheck2 className="h-4 w-4 text-slate-400" />
            <p className="font-medium text-slate-900">{req.name}</p>
            <Chip map={EV_STATUS} value={req.status} testid={`evidence-status-${req.id}`} />
            {req.mandatory && <span className="ts-chip ts-chip-danger">Obligatoria</span>}
          </div>
          <p className="mt-1 text-xs text-slate-500">Criterio de aceptación: {req.acceptance_criteria}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Tipo esperado: <span className="font-medium">{req.expected_type}</span> · Responsable: <strong>{userName(map, req.responsible_id)}</strong> · Revisor: <strong>{userName(map, req.reviewer_id)}</strong> · Límite: {fmtDate(req.due_date)}
          </p>
        </div>
        <div className="flex gap-2">
          {canSubmit && <Button size="sm" variant="outline" onClick={() => setSubmitOpen(true)} data-testid={`submit-evidence-${req.id}`}>Aportar evidencia</Button>}
          {canReview && (
            <>
              <Button size="sm" onClick={() => doReview("aceptar")} data-testid={`accept-evidence-${req.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Aceptar</Button>
              <Button size="sm" variant="destructive" onClick={() => setRejectOpen(true)} data-testid={`reject-evidence-${req.id}`}><X className="mr-1 h-3.5 w-3.5" /> Rechazar</Button>
            </>
          )}
        </div>
      </div>

      {(req.evidences || []).length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
          {req.evidences.map((ev) => (
            <div key={ev.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`evidence-version-${ev.id}`}>
              <span className="font-mono text-slate-400">v{ev.version}</span>
              <span className="text-slate-700">{ev.title}</span>
              <Chip map={EV_STATUS} value={ev.status} />
              <span className="text-slate-400">por {userName(map, ev.submitted_by)} · {fmtDateTime(ev.submitted_at)}</span>
              {ev.review?.reason && <span className="text-red-600">— {ev.review.decision === "rechazar" ? "Rechazada" : "Nota"}: {ev.review.reason}</span>}
              {ev.review && <span className="text-slate-400">(revisada por {userName(map, ev.review.by)})</span>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent data-testid="submit-evidence-dialog">
          <DialogHeader><DialogTitle>Aportar evidencia — {req.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de evidencia</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="evidence-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{EVIDENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Título</Label><input className="flex h-9 w-full rounded-sm border border-input bg-white px-3 text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={req.name} data-testid="evidence-title-input" /></div>
            <div><Label>Descripción / referencia</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe qué aportas, dónde está el documento o qué sistema lo registra…" data-testid="evidence-description-input" /></div>
            <Button onClick={doSubmit} className="w-full" data-testid="evidence-submit-confirm">Enviar a revisión</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-evidence-dialog">
          <DialogHeader><DialogTitle>Rechazar evidencia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">El rechazo devolverá el requisito al responsable y creará una tarea de corrección. El motivo es obligatorio.</p>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo del rechazo (obligatorio)…" data-testid="reject-motivo-input" />
            <Button variant="destructive" onClick={() => doReview("rechazar")} disabled={!motivo.trim()} className="w-full" data-testid="reject-evidence-confirm">Rechazar con motivo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ApprovalCard({ appr, me, onChanged }) {
  const { map } = useUsers();
  const [dialog, setDialog] = useState(null); // 'aprobar' | 'rechazar' | 'cambios'
  const [comment, setComment] = useState("");
  const pending = ["solicitada", "en_revision", "cambios_solicitados"].includes(appr.status);
  const iAmApprover = appr.approver_id === me?.id;
  const iAmRequester = appr.requester_id === me?.id;

  const decide = async (decision, c = "") => {
    try {
      await api.post(`/approvals/${appr.id}/decision`, { decision, comment: c });
      toast.success("Decisión registrada");
      setDialog(null); setComment("");
      onChanged?.();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) toast.error("No se puede aprobar", { description: reasons.map((r) => r.message).join(" ") });
      else toast.error(apiError(e));
    }
  };

  return (
    <div className="ts-card p-4" data-testid={`approval-${appr.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-slate-900">{appr.subject}</p>
            <Chip map={APPR_STATUS} value={appr.status} testid={`approval-status-${appr.id}`} />
            {appr.maker_checker && <span className="ts-chip ts-chip-info">maker-checker</span>}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Solicitante: <strong>{userName(map, appr.requester_id)}</strong> → Aprobador: <strong>{userName(map, appr.approver_id)}</strong> · Límite: {fmtDate(appr.due_date)}
          </p>
          {appr.info && <p className="mt-1 rounded-sm border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-600" data-testid={`approval-info-${appr.id}`}>{appr.info}</p>}
          {appr.expediente && (
            <Link to={`/expedientes/${appr.expediente_id}`} className="mt-0.5 block text-xs text-blue-700 hover:underline">
              {appr.expediente.code} — {appr.expediente.title}
            </Link>
          )}
          {appr.decision_comment && <p className="mt-1 text-xs text-slate-600">Comentario de decisión: {appr.decision_comment}</p>}
          {iAmRequester && pending && <p className="mt-1 text-xs text-amber-700">Eres el solicitante: no puedes aprobar tu propia solicitud (separación de funciones).</p>}
        </div>
        {pending && iAmApprover && !iAmRequester && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setDialog("aprobar")} data-testid={`approve-button-${appr.id}`}><ThumbsUp className="mr-1 h-3.5 w-3.5" /> Aprobar</Button>
            <Button size="sm" variant="outline" onClick={() => setDialog("cambios")} data-testid={`changes-button-${appr.id}`}>Solicitar cambios</Button>
            <Button size="sm" variant="destructive" onClick={() => setDialog("rechazar")} data-testid={`reject-approval-${appr.id}`}><ThumbsDown className="mr-1 h-3.5 w-3.5" /> Rechazar</Button>
          </div>
        )}
      </div>
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="approval-decision-dialog">
          <DialogHeader><DialogTitle>{dialog === "rechazar" ? "Rechazar aprobación" : dialog === "cambios" ? "Solicitar cambios" : "Aprobar"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{dialog === "rechazar" ? "El motivo es obligatorio y se devolverá el expediente al responsable." : dialog === "cambios" ? "Indica las instrucciones de corrección (obligatorio)." : "Puedes añadir un comentario (opcional). La decisión queda registrada con tu identidad."}</p>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="approval-comment-input" placeholder={dialog === "aprobar" ? "Comentario (opcional)…" : "Motivo / instrucciones…"} />
            <Button variant={dialog === "rechazar" ? "destructive" : "default"} disabled={dialog !== "aprobar" && !comment.trim()} onClick={() => decide(dialog, comment)} className="w-full" data-testid="approval-decision-confirm">Confirmar decisión</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AICard({ a, onChanged }) {
  const { map } = useUsers();
  const [dialog, setDialog] = useState(null); // corregir | rechazar
  const [text, setText] = useState("");
  const reviewable = ["resultado_disponible", "requiere_validacion"].includes(a.validation_status);

  const act = async (action, payload = {}) => {
    try {
      await api.post(`/ai-analyses/${a.id}/validate`, { action, ...payload });
      toast.success("Revisión registrada");
      setDialog(null); setText("");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="ts-card p-4" data-testid={`ai-analysis-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <p className="font-medium text-slate-900">{a.analysis_type}</p>
            <Chip map={AI_STATUS} value={a.validation_status} testid={`ai-status-${a.id}`} />
            <span className="font-mono text-[10px] text-slate-400">{a.model}</span>
          </div>
          <p className="mt-2 text-sm text-slate-700">{a.result}</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-slate-500">Confianza:</span>
            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full ${a.confidence < 0.7 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${a.confidence * 100}%` }} />
            </div>
            <span className={`font-mono text-xs font-semibold ${a.confidence < 0.7 ? "text-red-600" : "text-emerald-600"}`}>{a.confidence}</span>
          </div>
          <div className="mt-3 space-y-1">
            {(a.findings || []).map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`h-1.5 w-1.5 rounded-full ${f.severity === "alto" ? "bg-red-500" : f.severity === "medio" ? "bg-amber-500" : "bg-slate-300"}`} />
                <span className="text-slate-700">{f.text}</span>
                <span className="rounded-sm bg-slate-100 px-1 font-mono text-[10px] text-slate-500">fuente: {f.source}</span>
              </div>
            ))}
          </div>
          {(a.pending_data || []).length > 0 && (
            <p className="mt-2 text-xs text-amber-700">Datos pendientes de confirmar: {a.pending_data.join("; ")}</p>
          )}
          {a.recommendation && <p className="mt-2 text-xs text-slate-600"><strong>Recomendación:</strong> {a.recommendation}</p>}
          {a.human_correction && (
            <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              <strong>Corrección humana ({userName(map, a.reviewer_id)}):</strong> {a.human_correction}
            </div>
          )}
          <p className="mt-2 text-[10px] text-slate-400">Generado: {fmtDateTime(a.date)}{a.reviewer_id ? ` · Revisado por ${userName(map, a.reviewer_id)}` : " · Sin revisión humana"}</p>
        </div>
        {reviewable && (
          <div className="flex flex-col gap-1.5">
            <Button size="sm" variant="outline" onClick={() => act("validar")} data-testid={`ai-validate-${a.id}`}>Validar</Button>
            <Button size="sm" variant="outline" onClick={() => setDialog("corregir")} data-testid={`ai-correct-${a.id}`}>Corregir</Button>
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDialog("rechazar")} data-testid={`ai-reject-${a.id}`}>Rechazar</Button>
          </div>
        )}
      </div>
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="ai-review-dialog">
          <DialogHeader><DialogTitle>{dialog === "corregir" ? "Corregir resultado IA" : "Rechazar resultado IA"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={dialog === "corregir" ? "Describe la corrección (se conservará el resultado inicial como referencia)…" : "Motivo del rechazo (obligatorio)…"} data-testid="ai-review-input" />
            <Button className="w-full" disabled={!text.trim()} onClick={() => act(dialog === "corregir" ? "corregir" : "rechazar", dialog === "corregir" ? { correction: text } : { motivo: text })} data-testid="ai-review-confirm">Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
